import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Barrie = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.barrie:About Barrie Paragraph 1">
            Barrie, situated in{' '}
            <LinkText href="/rental-listings/ontario">Ontario</LinkText>, is
            renowned for its scenic landscapes, recreational opportunities, and
            strong community spirit. Its waterfront along Lake Simcoe offers a
            stunning backdrop for outdoor activities, while proximity to forests
            and Kempenfelt Bay caters to hiking, biking, and skiing enthusiasts.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.barrie:About Barrie Paragraph 2">
            The city’s vibrant downtown, linked closely to{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>,
            hosts an array of shops, restaurants, and entertainment venues,
            fostering a dynamic social and cultural scene. Barrie’s cultural
            events and festivals throughout the year contribute to its engaging
            atmosphere. With esteemed educational institutions, the city ensures
            quality education for families and students. The close-knit
            community values inclusivity, volunteerism, and local initiatives,
            promoting a sense of belonging. Located an hour from Toronto, Barrie
            offers a balanced lifestyle by combining natural beauty with urban
            convenience.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,300 - $2,000',
          bed2: '$1,800 - $2,600',
          bed3: '$2,200 - $3,100'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.barrie:Apartment Hunting in Barrie Paragraph 1">
            Barrie is an attractive city of Ontario known for its thriving
            community and diverse housing options. While rental prices in Barrie
            are generally more affordable compared to Toronto, they have been
            steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>cheap rentals in Barrie</LinkText>,
            especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.barrie:Apartments for rent Barrie Paragraph">
              <LinkText href={links.apartment}>Apartments for rent</LinkText>{' '}
              are prevalent in the more urban areas of Barrie. It’s a popular
              option for young professionals, couples, and small families. These
              units often come with added amenities such as fitness centers or
              community spaces. Compared to other Canadian cities, you’re more
              likely to find a bargain in Barrie.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.barrie:Houses for rent Barrie Paragraph">
              There are plenty of{' '}
              <LinkText href={links.house}>
                houses for rent in the suburban parts of Barrie
              </LinkText>
              , which offer more living space, private yards, and multiple
              bedrooms. It’s particularly suitable for larger families or groups
              willing to share. Both partial houses and entire houses for rent
              are common, as well as both new builds and older homes from the
              city’s earlier days.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.barrie:Condos for rent Barrie Paragraph">
              In more urban areas like Downtown Barrie,{' '}
              <LinkText href={links.apartment}>
                condos for rent in Barrie
              </LinkText>{' '}
              are a common sight. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.barrie:Short term rentals Barrie Paragraph">
              Barrie, with its growing economy, presents various employment
              opportunities, and while it doesn’t host extensive academic
              institutions within its borders, it lies in proximity to several
              reputable schools and colleges in the broader region, attracting
              students and educators alike. The demand for{' '}
              <LinkText href={links.shortTerm}>
                short-term rentals in Barrie
              </LinkText>{' '}
              experience an uptick, especially in the summer, catering to
              tourists exploring the city’s lakeside attractions, and during the
              school year, accommodating students and temporary workers. The
              added flexibility is an appealing prospect for many renters, and a
              key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.barrie:Living in Barrie Paragraph">
          Living in Barrie, Ontario, is particularly appealing for those who
          enjoy a blend of suburban tranquility and access to big-city
          amenities, given its strategic proximity to{' '}
          <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>.
          Barrie stands as a recreational haven with its pristine waterfront on
          Lake Simcoe, offering activities ranging from summer beach outings to
          winter sports. The community provides a variety of rental options
          suitable for professionals, students, and families, often at a more
          approachable price point compared to Toronto’s bustling market.
          Residents and visitors alike revel in Barrie’s rich cultural
          offerings, including live theatre performances, art exhibitions, and
          seasonal festivals. This balance of local and metropolitan life,
          coupled with an inviting, inclusive community, makes Barrie an
          attractive place to live for individuals craving both serenity and the
          excitement of a nearby major city.
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.barrie:Popular Neighbourhoods Barrie Paragraph">
          Like any city, Barrie is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Barrie, Allandale, Masonville, Holly, Painswick, Ardagh, East
          Bayfield, and South End.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.barrie:Local Landmarks Barrie Paragraph">
          Barrie, Ontario, boasts several landmarks that capture the city’s
          unique blend of natural beauty, cultural richness, and historical
          depth. The Spirit Catcher, a monumental sculpture situated along the
          beautiful shores of Kempenfelt Bay, stands as an iconic symbol of the
          city, reflecting its cultural vitality and artistic spirit. The
          historic Downtown area, characterized by its charming buildings,
          boutique shops, and an array of restaurants, serves as a central
          gathering spot and a testament to Barrie’s vibrant community life.
          Nature enthusiasts frequently visit Sunnidale Park Arboretum, offering
          a serene escape with its sprawling gardens, walking trails, and a
          diverse collection of plant and tree species. Additionally, history
          buffs are drawn to the Simcoe County Museum, a regional treasure where
          exhibitions span from indigenous history to pioneer life, providing
          comprehensive insights into the area’s rich past.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Barrie
